import React, { useEffect } from 'react';
import { Form, Input, DatePicker, Select, Checkbox, Button, Divider, Row, Col, Typography, Layout, Menu } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import './SupportForm.css';

const { Content, Header } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const SupportForm = () => {
  const [form] = Form.useForm();

  const onFinish = (values) => {
    console.log('Form Values:', values);
    alert('Form submitted successfully!');
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
    alert('Please correct the errors in the form.');
  };

  // Reset military fields when veteran status changes
  const handleVeteranStatusChange = (value) => {
    if (value === 'no') {
      form.setFieldsValue({
        militaryServiceDetails: '',
        militaryDischargeStatus: undefined,
      });
    }
  };

  // Ensure initial state of fields
  useEffect(() => {
    const veteranStatus = form.getFieldValue('veteranStatus');
    if (veteranStatus !== 'yes') {
      form.setFieldsValue({
        militaryServiceDetails: '',
        militaryDischargeStatus: undefined,
      });
    }
  }, [form]);

  return (
    <Layout style={{ minHeight: '100vh', background: '#f9fafb' }}>
      <Header className="app-header">
        <div className="logo">
          <UserOutlined style={{ color: '#1890ff', marginRight: 8 }} />
          NH Guide
        </div>
        <Menu mode="horizontal" className="nav-menu">
          <Menu.Item key="profile">Profile</Menu.Item>
          <Menu.Item key="support">Support</Menu.Item>
          <Menu.Item key="contacts">Contacts</Menu.Item>
          <Menu.Item key="logout">Logout</Menu.Item>
        </Menu>
      </Header>
      <Content style={{ padding: '40px 20px', display: 'flex', justifyContent: 'center' }}>
        <div className="form-container">
          <Title level={2} className="form-title">Your Support Information</Title>
          <Text className="form-description">
            Please fill out the following details to connect with support services.
          </Text>
          <Form
            form={form}
            name="support_form"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            layout="vertical"
            className="support-form"
            initialValues={{
              veteranStatus: 'no',
              militaryServiceDetails: '',
              militaryDischargeStatus: undefined,
            }}
          >
            {/* Part A: Personal Information */}
            <Divider orientation="left" className="section-title">Part A: Personal Information</Divider>
            <Row gutter={[24, 24]}>
              <Col span={8}>
                <Form.Item
                  label="First Name *"
                  name="firstName"
                  rules={[{ required: true, message: 'Please enter your first name!' }]}
                >
                  <Input placeholder="Enter your first name" className="form-input" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Middle Initial"
                  name="middleInitial"
                >
                  <Input placeholder="Enter your middle initial" className="form-input" maxLength={1} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Last Name *"
                  name="lastName"
                  rules={[{ required: true, message: 'Please enter your last name!' }]}
                >
                  <Input placeholder="Enter your last name" className="form-input" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[24, 24]}>
              <Col span={8}>
                <Form.Item
                  label="Last 4 Digits of SSN *"
                  name="ssnLastFour"
                  rules={[
                    { required: true, message: 'Please enter the last 4 digits of your SSN!' },
                    { pattern: /^\d{4}$/, message: 'Please enter exactly 4 digits!' },
                  ]}
                >
                  <Input placeholder="XXXX" className="form-input" maxLength={4} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Date of Birth *"
                  name="dateOfBirth"
                  rules={[{ required: true, message: 'Please select your date of birth!' }]}
                >
                  <DatePicker className="form-input" placeholder="Select date" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Gender *"
                  name="gender"
                  rules={[{ required: true, message: 'Please select your gender!' }]}
                >
                  <Select placeholder="Select gender" className="form-input">
                    <Option value="male">Male</Option>
                    <Option value="female">Female</Option>
                    <Option value="non-binary">Non-Binary</Option>
                    <Option value="prefer-not-to-say">Prefer Not to Say</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[24, 24]}>
              <Col span={12}>
                <Form.Item
                  label="Race/Ethnicity *"
                  name="raceEthnicity"
                  rules={[{ required: true, message: 'Please select your race/ethnicity!' }]}
                >
                  <Select placeholder="Select race/ethnicity" className="form-input">
                    <Option value="american-indian">American Indian or Alaska Native</Option>
                    <Option value="asian">Asian</Option>
                    <Option value="black">Black or African American</Option>
                    <Option value="hispanic">Hispanic or Latino</Option>
                    <Option value="white">White</Option>
                    <Option value="other">Other</Option>
                    <Option value="prefer-not-to-say">Prefer Not to Say</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Veteran Status *"
                  name="veteranStatus"
                  rules={[{ required: true, message: 'Please select your veteran status!' }]}
                >
                  <Select
                    placeholder="Select veteran status"
                    className="form-input"
                    onChange={(value) => handleVeteranStatusChange(value)}
                  >
                    <Option value="yes">Yes</Option>
                    <Option value="no">No</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[24, 24]}>
              <Col span={12}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) => prevValues.veteranStatus !== currentValues.veteranStatus}
                >
                  {({ getFieldValue }) => (
                    <Form.Item
                      label="Military Service Details"
                      name="militaryServiceDetails"
                      rules={[
                        { required: getFieldValue('veteranStatus') === 'yes', message: 'Please provide military service details!' },
                      ]}
                    >
                      <Input.TextArea
                        placeholder="Enter details (e.g., branch, years of service)"
                        className="form-textarea"
                        disabled={getFieldValue('veteranStatus') !== 'yes'}
                      />
                    </Form.Item>
                  )}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) => prevValues.veteranStatus !== currentValues.veteranStatus}
                >
                  {({ getFieldValue }) => (
                    <Form.Item
                      label="Military Discharge Status"
                      name="militaryDischargeStatus"
                      rules={[
                        { required: getFieldValue('veteranStatus') === 'yes', message: 'Please select your discharge status!' },
                      ]}
                    >
                      <Select
                        placeholder="Select discharge status"
                        className="form-input"
                        disabled={getFieldValue('veteranStatus') !== 'yes'}
                      >
                        <Option value="honorable">Honorable</Option>
                        <Option value="general">General</Option>
                        <Option value="other-than-honorable">Other Than Honorable</Option>
                        <Option value="dishonorable">Dishonorable</Option>
                        <Option value="not-applicable">Not Applicable</Option>
                      </Select>
                    </Form.Item>
                  )}
                </Form.Item>
              </Col>
            </Row>

            {/* Part B: Support System and Housing Information */}
            <Divider orientation="left" className="section-title">Part B: Support System and Housing Information</Divider>
            <Row gutter={[24, 24]}>
              <Col span={12}>
                <Form.Item
                  label="Support System Record Number"
                  name="supportRecordNumber"
                >
                  <Input placeholder="Enter your support record number" className="form-input" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Current Housing Status *"
                  name="housingStatus"
                  rules={[{ required: true, message: 'Please select your current housing status!' }]}
                >
                  <Select placeholder="Select housing status" className="form-input">
                    <Option value="homeless">Homeless</Option>
                    <Option value="living-in-car">Living in a Car</Option>
                    <Option value="emergency-shelter">Emergency Shelter</Option>
                    <Option value="temporary-housing">Temporary Housing</Option>
                    <Option value="stable-housing">Stable Housing</Option>
                    <Option value="other">Other</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[24, 24]}>
              <Col span={12}>
                <Form.Item
                  label="Current Address"
                  name="currentAddress"
                >
                  <Input.TextArea placeholder="Enter your current address" className="form-textarea" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Location Where Housing Was Lost"
                  name="housingLostLocation"
                >
                  <Input.TextArea placeholder="Enter location where housing was lost" className="form-textarea" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[24, 24]}>
              <Col span={24}>
                <Form.Item
                  label="Location Where You Spend Most of Your Time"
                  name="mostTimeLocation"
                >
                  <Input.TextArea placeholder="Enter location where you spend most of your time" className="form-textarea" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[24, 24]}>
              <Col span={24}>
                <Form.Item
                  label="Connect to Local Services"
                  name="connectToServices"
                >
                  <Checkbox.Group>
                    <Row gutter={[16, 16]}>
                      <Col span={8}><Checkbox value="housing-assistance">Housing Assistance</Checkbox></Col>
                      <Col span={8}><Checkbox value="food-assistance">Food Assistance</Checkbox></Col>
                      <Col span={8}><Checkbox value="healthcare">Healthcare</Checkbox></Col>
                      <Col span={8}><Checkbox value="mental-health">Mental Health Services</Checkbox></Col>
                      <Col span={8}><Checkbox value="job-training">Job Training</Checkbox></Col>
                      <Col span={8}><Checkbox value="legal-aid">Legal Aid</Checkbox></Col>
                    </Row>
                  </Checkbox.Group>
                </Form.Item>
              </Col>
            </Row>

            {/* Submit Button */}
            <Form.Item>
              <Button type="primary" htmlType="submit" className="submit-button">
                save
              </Button>
            </Form.Item>
          </Form>
        </div>
      </Content>
    </Layout>
  );
};

export default SupportForm;